import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, SegmentedControl } from '@mlm/ui';
import { Plus, Trash2 } from 'lucide-react';
import {
  rtuOverrideCount,
  rtuRemoveLevel,
  type RealtimeUnilevelMembershipMatrix,
  type RealtimeUnilevelProductConfig,
} from '@mlm/shared';
import {
  listCompPlanConfigs,
  listMemberships,
  publishCompPlanConfig,
  type CompPlanConfigRow,
  type Membership,
} from '../../lib/api';
import type { RealtimeUnilevelConfig } from '../compplan/realtime-unilevel-editor';

const PLAN_CODE = 'realtime_unilevel';

/** Latest-version config row for the realtime_unilevel plan, or null. */
function latestConfig(rows: CompPlanConfigRow[]): CompPlanConfigRow | null {
  return rows
    .filter((r) => r.planCode === PLAN_CODE)
    .reduce<CompPlanConfigRow | null>(
      (best, r) => (!best || r.version > best.version ? r : best),
      null,
    );
}

/**
 * Per-product view over the global `realtime_unilevel` comp config. The plan
 * stores one global blob `{ products: [{ product_id, level_amounts,
 * membership_matrix? }] }`; here we edit just this product's entry and splice
 * it back into the full blob, publishing a new config version (configs are
 * versioned, never mutated).
 *
 * Only rendered for super_admin when the plan is enabled (gated by the caller).
 */
export function ProductRealtimeUnilevelEditor({ productId }: { productId: string }) {
  const qc = useQueryClient();
  const configsQ = useQuery({
    queryKey: ['admin-compplan-configs'],
    queryFn: listCompPlanConfigs,
  });
  const membershipsQ = useQuery({ queryKey: ['memberships'], queryFn: listMemberships });

  const current = useMemo(() => latestConfig(configsQ.data ?? []), [configsQ.data]);
  const fullConfig = (current?.config as RealtimeUnilevelConfig | undefined) ?? { products: [] };
  const existing = fullConfig.products?.find((p) => p.product_id === productId);

  // Local draft of THIS product's entry. `null` = follow server.
  const [draft, setDraft] = useState<RealtimeUnilevelProductConfig | null>(null);
  const entry: RealtimeUnilevelProductConfig = draft ??
    existing ?? { product_id: productId, level_amounts: [0] };
  const levels = entry.level_amounts.map((a) => Number(a) || 0);
  const matrix = entry.membership_matrix;
  const [shownLevel, setShownLevel] = useState(0);
  const level = Math.min(shownLevel, levels.length - 1);

  const save = useMutation({
    mutationFn: () => {
      const others = (fullConfig.products ?? []).filter((p) => p.product_id !== productId);
      // Drop the row entirely when there's nothing to pay (all-zero defaults
      // and no membership cell either).
      const keep =
        levels.some((a) => a > 0) ||
        (matrix?.enabled === true && levels.some((_, k) => rtuOverrideCount(matrix, k) > 0));
      const nextProducts = keep ? [...others, { ...entry, product_id: productId }] : others;
      return publishCompPlanConfig({
        planCode: PLAN_CODE,
        config: { ...fullConfig, products: nextProducts },
      });
    },
    meta: { successMessage: 'Unilevel pricing saved.' },
    onSuccess: () => {
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['admin-compplan-configs'] });
    },
  });

  function patch(next: Partial<RealtimeUnilevelProductConfig>) {
    setDraft({ ...entry, ...next });
  }
  function setAmount(lvl: number, amount: number) {
    const next = [...levels];
    next[lvl] = amount;
    patch({ level_amounts: next });
  }
  function addLevel() {
    patch({ level_amounts: [...levels, 0] });
  }
  function removeLevel(lvl: number) {
    setDraft(rtuRemoveLevel({ ...entry, level_amounts: levels }, lvl));
    if (shownLevel >= lvl && shownLevel > 0) setShownLevel(shownLevel - 1);
  }
  function setMatrix(next: RealtimeUnilevelMembershipMatrix) {
    patch({ membership_matrix: next });
  }

  if (configsQ.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-5">
      <p className="text-[11px] text-muted-foreground">
        Fires the instant a member buys this product (no maintenance / qualification). Pays a FIXED
        cash amount per level: Level 0 is the buyer's own cashback; Levels 1+ pay the sponsor
        uplines. Amounts scale by quantity. A level with no upline is unpaid. Saving here updates
        the shared Realtime Unilevel comp-plan config.
      </p>

      <table className="w-full overflow-hidden rounded-md border text-sm">
        <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
          <tr>
            <th className="w-40 px-3 py-2 font-medium">Level</th>
            <th className="px-3 py-2 font-medium">
              {matrix?.enabled ? 'Default amount' : 'Amount'}
            </th>
            <th className="w-12 px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {levels.map((amt, lvl) => (
            <tr key={lvl} className="border-t">
              <td className="px-3 py-2 text-xs">
                {lvl === 0 ? (
                  <span className="font-mono">L0 — buyer cashback</span>
                ) : (
                  <span className="font-mono">L{lvl}</span>
                )}
              </td>
              <td className="px-3 py-2">
                <input
                  type="number"
                  className="h-8 w-36 rounded-md border bg-background px-2 text-sm"
                  value={amt}
                  step={0.01}
                  min={0}
                  onChange={(e) => setAmount(lvl, Number(e.target.value) || 0)}
                />
              </td>
              <td className="px-3 py-2">
                {lvl > 0 && (
                  <button
                    type="button"
                    onClick={() => removeLevel(lvl)}
                    className="text-muted-foreground transition-colors hover:text-destructive"
                    aria-label={`Remove level ${lvl}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="space-y-3 rounded-md border p-3">
        <label className="flex items-start gap-2">
          <Checkbox
            className="mt-0.5"
            checked={matrix?.enabled === true}
            onCheckedChange={(v) => setMatrix({ ...matrix, enabled: v === true })}
          />
          <span className="space-y-0.5">
            <span className="block text-sm font-medium">Vary by membership</span>
            <span className="block text-[11px] text-muted-foreground">
              Set a different amount for each earner and buyer membership. A blank cell pays the
              level&apos;s default amount above; 0 pays nothing for that pair.
            </span>
          </span>
        </label>

        {matrix?.enabled ? (
          membershipsQ.isLoading ? (
            <p className="text-xs text-muted-foreground">Loading memberships…</p>
          ) : (membershipsQ.data ?? []).length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No memberships yet. Add them under Memberships first.
            </p>
          ) : (
            <MembershipGrid
              level={level}
              levels={levels}
              onLevel={setShownLevel}
              memberships={membershipsQ.data ?? []}
              matrix={matrix}
              onChange={setMatrix}
            />
          )
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={addLevel}>
          <Plus className="mr-1 h-3 w-3" />
          Add level
        </Button>
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save unilevel'}
        </Button>
        {draft !== null && !save.isPending && (
          <span className="text-xs text-muted-foreground">Unsaved changes</span>
        )}
        {save.isSuccess && draft === null && <span className="text-xs text-success">Saved</span>}
        {save.error != null && <span className="text-xs text-destructive">Save failed</span>}
      </div>
    </div>
  );
}

/** Blank input = no cell (falls back to the level default). */
function cellValue(v: number | string | null | undefined): string {
  return v === undefined || v === null ? '' : String(v);
}

/**
 * One level's membership grid. Level 0 is a single row (the buyer is the
 * earner); Levels 1+ are earner (rows) × buyer (columns). Keys are membership
 * codes; each cell's placeholder shows the default it falls back to.
 */
function MembershipGrid({
  level,
  levels,
  onLevel,
  memberships,
  matrix,
  onChange,
}: {
  level: number;
  levels: number[];
  onLevel: (level: number) => void;
  memberships: Membership[];
  matrix: RealtimeUnilevelMembershipMatrix;
  onChange: (next: RealtimeUnilevelMembershipMatrix) => void;
}) {
  const fallback = levels[level] ?? 0;
  const options = levels.map((_, k) => {
    const n = rtuOverrideCount(matrix, k);
    return { value: k, label: `${k === 0 ? 'L0 cashback' : `L${k}`}${n ? ` · ${n}` : ''}` };
  });

  function setCashback(buyer: string, raw: string) {
    const cashback = { ...(matrix.cashback ?? {}) };
    if (raw === '') delete cashback[buyer];
    else cashback[buyer] = Number(raw) || 0;
    onChange({ ...matrix, cashback });
  }
  function setCell(earner: string, buyer: string, raw: string) {
    const all = { ...(matrix.levels ?? {}) };
    const grid = { ...(all[String(level)] ?? {}) };
    const row = { ...(grid[earner] ?? {}) };
    if (raw === '') delete row[buyer];
    else row[buyer] = Number(raw) || 0;
    grid[earner] = row;
    all[String(level)] = grid;
    onChange({ ...matrix, levels: all });
  }
  function clearLevel() {
    if (level === 0) onChange({ ...matrix, cashback: {} });
    else onChange({ ...matrix, levels: { ...(matrix.levels ?? {}), [String(level)]: {} } });
  }

  const input = (value: string, onValue: (raw: string) => void, label: string) => (
    <input
      type="number"
      inputMode="decimal"
      className="h-8 w-20 rounded-md border bg-background px-2 text-right text-xs tabular-nums placeholder:text-muted-foreground/60"
      value={value}
      placeholder={String(fallback)}
      step={0.01}
      min={0}
      aria-label={label}
      onChange={(e) => onValue(e.target.value)}
    />
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SegmentedControl
          size="sm"
          ariaLabel="Level"
          value={level}
          onChange={onLevel}
          options={options}
        />
        {rtuOverrideCount(matrix, level) > 0 ? (
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground hover:text-destructive"
            onClick={clearLevel}
          >
            Clear this level
          </Button>
        ) : null}
      </div>
      <p className="text-[11px] text-muted-foreground">
        {level === 0
          ? `Cashback the buyer gets, by the buyer's membership. Blank = ${fallback}.`
          : `What a Level ${level} upline earns, by the upline's membership (rows) and the buyer's membership (columns). Blank = ${fallback}.`}
      </p>
      <div className="overflow-x-auto rounded-md border">
        <table className="text-xs">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="sticky left-0 z-10 bg-muted px-3 py-2 text-left font-medium">
                {level === 0 ? '' : 'Earner ↓ · Buyer →'}
              </th>
              {memberships.map((m) => (
                <th
                  key={m.id}
                  className="max-w-[7rem] px-2 py-2 text-right font-medium"
                  title={m.code}
                >
                  <span className="block truncate">{m.name}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {level === 0 ? (
              <tr className="border-t">
                <th className="sticky left-0 z-10 bg-background px-3 py-1.5 text-left font-medium">
                  Cashback
                </th>
                {memberships.map((m) => (
                  <td key={m.id} className="px-2 py-1.5 text-right">
                    {input(
                      cellValue(matrix.cashback?.[m.code]),
                      (raw) => setCashback(m.code, raw),
                      `Cashback for a ${m.name} buyer`,
                    )}
                  </td>
                ))}
              </tr>
            ) : (
              memberships.map((earner) => (
                <tr key={earner.id} className="border-t">
                  <th
                    className="sticky left-0 z-10 max-w-[10rem] bg-background px-3 py-1.5 text-left font-medium"
                    title={earner.code}
                  >
                    <span className="block truncate">{earner.name}</span>
                  </th>
                  {memberships.map((buyer) => (
                    <td key={buyer.id} className="px-2 py-1.5 text-right">
                      {input(
                        cellValue(matrix.levels?.[String(level)]?.[earner.code]?.[buyer.code]),
                        (raw) => setCell(earner.code, buyer.code, raw),
                        `Level ${level}: ${earner.name} earner, ${buyer.name} buyer`,
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
